import { without } from 'lodash';
import {
  labeled,
  guidRequired,
  enumRequired,
  stringOptional,
  timeStampEpochRequired,
  timeStampEpochOptional,
  stringRequired
} from '../../util/joi';
import { YesNoOption, yesNoOptions } from '../global/YesNoOption.enum';
import { CreditRecordOption, creditRecordOptions } from '../global/CreditRecordOption.enum';

export const loanworksSchema = (Joi: any, label: string) =>
  Joi.object().keys({
    bankruptcy: Joi.array()
      .items(
        Joi.object()
          .keys({
            idContact: guidRequired(Joi),
            response: enumRequired(
              Joi,
              yesNoOptions,
              labeled(label, 'Bankruptcy - Has any applicant ever been declared bankrupt?')
            ),
            comments: stringOptional(Joi),
            dischargedDate: timeStampEpochOptional(Joi).label(
              labeled(label, 'Bankruptcy - Discharge Date')
            )
          })
          .when('.response.name', {
            is: YesNoOption.yes.name,
            then: Joi.object().keys({
              comments: stringRequired(Joi, 1, 4096).label(
                labeled(label, 'Bankruptcy - Comments')
              )
            })
          })
          .when('.response.name', {
            is: YesNoOption.yes_discharged.name,
            then: Joi.object().keys({
              dischargedDate: timeStampEpochRequired(Joi).label(
                labeled(label, 'Bankruptcy - Discharge Date')
              )
            })
          })
      )
      .min(1)
      .required()
      .label(labeled(label, 'Bankruptcy'))
  });

export const homeLoanSchema = (Joi: any, label: string) => {
  const availableYesNoOptions = without(
    yesNoOptions,
    YesNoOption.none.name,
    YesNoOption.yes_discharged.name,
    YesNoOption.not_important.name,
    YesNoOption.unsure.name
  );

  return loanworksSchema(Joi, label)
    .keys({
      applicantHadProblems: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Has either applicant ever had any problems meeting any of your fixed commitments including mobile phone payments?'
        )
      ),
      applicantBeenOfficerOrShareholder: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Has either applicant ever been an officer or shareholder of any company of which a manager, receiver, and/or liquidator has been appointed?'
        )
      ),
      applicantHaveUnsatisfiedJudgements: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Does either applicant have any unsatisfied judgement(s) entered in any court against either applicant or any company of which either applicant are/were a Shareholder/Officer?'
        )
      ),
      applicantSubmittedApplicationToOtherCreditProviders: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Have any applicants simultaneously applied to any other Credit Provider(s) for this loan?'
        )
      )
    })
    .when('.applicantHadProblems.name', {
      is: YesNoOption.yes.name,
      then: Joi.object().keys({
        applicantHadProblemsComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'Has either applicant ever had any problems meeting any of your fixed commitments including mobile phone payments? - Comments'
          )
        )
      })
    })
    .when('.applicantBeenOfficerOrShareholder.name', {
      is: YesNoOption.yes.name,
      then: Joi.object().keys({
        applicantBeenOfficerOrShareholderComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'Has either applicant ever been an officer or shareholder of any company of which a manager, receiver, and/or liquidator has been appointed? - Comments'
          )
        )
      })
    })
    .when('.applicantHaveUnsatisfiedJudgements.name', {
      is: YesNoOption.yes.name,
      then: Joi.object().keys({
        applicantHaveUnsatisfiedJudgementsComments: stringRequired(Joi, 1, 4096).label(
          labeled(
            label,
            'Does either applicant have any unsatisfied judgement(s) entered in any court against either applicant or any company of which either applicant are/were a Shareholder/Officer? - Comments'
          )
        )
      })
    })
    .when('.applicantSubmittedApplicationToOtherCreditProviders.name', {
      is: YesNoOption.yes.name,
      then: Joi.object().keys({
        applicantSubmittedApplicationToOtherCreditProvidersComments: stringRequired(
          Joi,
          1,
          4096
        ).label(
          labeled(
            label,
            'Have any applicants simultaneously applied to any other Credit Provider(s) for this loan? - Comments'
          )
        )
      })
    });
};

export const assetFinanceSchema = (Joi: any, label: string) => {
  const availableCreditRecordOptions = without(
    creditRecordOptions,
    CreditRecordOption.none.name
  );

  return homeLoanSchema(Joi, label)
    .keys({
      creditRecord: enumRequired(
        Joi,
        availableCreditRecordOptions,
        labeled(label, "What is applicant's credit record?")
      )
    })
    .when('.creditRecord.name', {
      is: CreditRecordOption.bad.name,
      then: Joi.object().keys({
        creditRecordComments: stringRequired(Joi, 1, 4096).label(
          labeled(label, "What is applicant's credit record? - Comments")
        )
      })
    });
};

export default {
  loanworksSchema,
  homeLoanSchema,
  assetFinanceSchema
};
